import React, { useCallback, useEffect, useState } from 'react';
import { secureFetch } from '../../supabaseService';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function listPathForRole(role) {
  if (role === 'operator') return '/api/operator/contract-hmr';
  if (role === 'owner') return '/api/owner/contract-hmr';
  if (role === 'admin') return '/api/admin/contract-hmr';
  return '/api/client/contract-hmr';
}

/**
 * Additive contract HMR co-sign panel.
 * status_label from API uses evidentiary wording only (never Approved/Accepted).
 */
export default function ContractHmrPanel({
  booking,
  role = 'client',
  allowConfirm = false,
  allowLog = false,
}) {
  const bookingId = booking?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [hmrReading, setHmrReading] = useState('');
  const [hoursUsed, setHoursUsed] = useState('');
  const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!bookingId || booking?.booking_type !== 'contract') return;
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch(`${listPathForRole(role)}?bookingId=${encodeURIComponent(bookingId)}`);
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load HMR logs');
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load HMR logs');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [bookingId, booking?.booking_type, role]);

  useEffect(() => {
    load();
  }, [load]);

  if (!booking || booking.booking_type !== 'contract') return null;

  const handleConfirm = async (logId) => {
    if (!allowConfirm || busyId) return;
    setBusyId(logId);
    setError('');
    try {
      const result = await secureFetch(`/api/client/contract-hmr/${logId}/confirm`, {
        method: 'POST',
        body: '{}',
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Could not confirm HMR log');
      }
      await load();
    } catch (err) {
      setError(err?.message || 'Could not confirm HMR log');
    } finally {
      setBusyId(null);
    }
  };

  const handleLog = async () => {
    if (!allowLog || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const body = {
        bookingId,
        logDate,
        hmrReading: Number(hmrReading),
      };
      if (hoursUsed !== '') body.hoursUsed = Number(hoursUsed);
      const result = await secureFetch('/api/operator/contract-hmr', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Could not save HMR log');
      }
      setHmrReading('');
      setHoursUsed('');
      await load();
    } catch (err) {
      setError(err?.message || 'Could not save HMR log');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="my-2 rounded-lg border border-sky-400/25 bg-sky-400/5 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-sky-300">
          Daily HMR co-sign
        </span>
        <button
          type="button"
          onClick={load}
          className="rounded border border-sky-400/30 px-2 py-1 text-[10px] font-semibold text-sky-200"
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {allowLog && (
        <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-3">
          <label className="text-[11px] text-slate-400">
            Log date
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-2 py-1.5 text-slate-100"
            />
          </label>
          <label className="text-[11px] text-slate-400">
            Closing HMR reading
            <input
              type="number"
              min="0"
              step="0.1"
              value={hmrReading}
              onChange={(e) => setHmrReading(e.target.value)}
              placeholder="Meter reading"
              className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-2 py-1.5 text-slate-100"
            />
          </label>
          <label className="text-[11px] text-slate-400">
            Hours used (optional)
            <input
              type="number"
              min="0"
              step="0.1"
              value={hoursUsed}
              onChange={(e) => setHoursUsed(e.target.value)}
              placeholder="Auto from prior day"
              className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-2 py-1.5 text-slate-100"
            />
          </label>
          <button
            type="button"
            onClick={handleLog}
            disabled={submitting || hmrReading === ''}
            className="rounded-lg bg-sky-600/80 px-3 py-2 text-xs font-bold text-white disabled:opacity-50 md:col-span-3"
          >
            {submitting ? 'Saving…' : 'Submit today\'s HMR reading'}
          </button>
        </div>
      )}

      {error && (
        <p className="mb-2 text-[11px] text-red-400">{error}</p>
      )}

      {items.length === 0 && !loading ? (
        <p className="mb-0 text-[11px] text-slate-400">No HMR logs for this contract yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((row) => (
            <div
              key={row.id}
              className="rounded border border-white/10 bg-black/25 px-2.5 py-2 text-xs text-slate-300"
            >
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-sky-200">{formatDate(row.log_date)}</span>
                <span className="rounded-full border border-sky-400/30 px-2 py-0.5 text-[10px] text-sky-100">
                  {row.status_label || 'Operator-logged, pending confirmation'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                <div>
                  <p className="mb-0.5 text-[10px] uppercase text-slate-500">HMR reading</p>
                  <p className="m-0">{Number(row.hmr_reading).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="mb-0.5 text-[10px] uppercase text-slate-500">Hours used</p>
                  <p className="m-0">
                    {row.hours_used != null ? Number(row.hours_used).toLocaleString('en-IN') : '—'}
                  </p>
                </div>
                <div>
                  <p className="mb-0.5 text-[10px] uppercase text-slate-500">Logged at</p>
                  <p className="m-0">
                    {row.operator_logged_at
                      ? new Date(row.operator_logged_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      : '—'}
                  </p>
                </div>
              </div>
              {allowConfirm && row.is_pending && (
                <button
                  type="button"
                  onClick={() => handleConfirm(row.id)}
                  disabled={busyId === row.id}
                  className="mt-2 w-full rounded-lg bg-emerald-700/80 px-3 py-2 text-[11px] font-bold text-white disabled:opacity-50"
                >
                  {busyId === row.id ? 'Confirming…' : 'Confirm this HMR reading'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
